import Collection from './Collection'
import QueryBuilder from './QueryBuilder'
import Builder from './Builder'
import HasOne from './Relationships/HasOne'
import HasMany from './Relationships/HasMany'
import BelongsTo from './Relationships/BelongsTo'
import BelongsToMany from './Relationships/BelongsToMany'
import { ConnectionPool } from './Connection'
import { 
    ModelType, 
    ModelInstanceType, 
    CollectionInstanceType, 
    QueryInstanceType,
    HasManyInstanceType,
    HasOneInstanceType,
    BelongsToInstanceType,
    BelongsToManyInstanceType,
    RelativeArgAbstraction,
} from './types'

class Model {

    public static entity: any

    public static table: string

    protected relatives: any = {}

    protected originals: any = {}

    protected attributes: any = {}

    protected primaryKey: string = 'id'

    protected guarded: string[] = []

    protected fillable: string[] = []

    protected updatable: string[] = []

    protected updatedAtColumn: string = 'updated_at'

    protected createdAtColumn: string = 'created_at'

    protected timestamp: boolean = true

    protected deletable: boolean = true

    protected creatable: boolean = true

    protected exists: boolean = false

    protected use: 'table' | 'entity' | string = 'entity'

    constructor (props: any = {}) {

        const { exists, attributes } = props
        
        if(exists && attributes) {
            this.originals = {...attributes}
            this.attributes = {...attributes}
            this.exists = exists
        }
    }

    protected isFillable(key: string): boolean {

        if(this.exists && this.updatable.length) {

            return this.updatable.includes(key)
        }

        if (this.fillable.includes(key)) return true

        if (this.isGuarded(key)) return false

        return this.fillable.length === 0
    }

    protected isGuarded(key: string): boolean {

        return this.guarded.includes(key) || this.guarded.includes('*')
    }

    public isDeletable(): boolean {
        
        return this.deletable
    }

    protected syncAttrAndOrig(): void {

        this.originals = {...this.attributes}
    }

    protected getAttributes(): any {

        return this.attributes
    }

    protected getAttribute(key: string): any {

        if(typeof key === 'symbol') {

            return 'do not console.log(sampleinstance) use sampleinstance.toJSON()'
        }

        // If the key references is an attribute
        if (key in this.attributes) {

            return this.attributes[key]
        }

        // If the key already exists in the relativeships object we will return it
        const relative: string = key.indexOf('$') === 0 ? key.substr(1) : key
        
        if (relative in this.relatives) {
            
            return this.relatives[relative];
        }

    }

    protected setAttribute(key: string, value: any): void {

        if (this.isFillable(key)) {

            this.attributes[key] = value
        }
    }

    protected setAttributeRaw(key: string, value: any): void {

        this.attributes[key] = value
    }

    protected getAttributeChanges(): any {

        const originals = this.originals

        const attributes = this.attributes

        const changes = Object.keys(attributes).filter((key: string) => {

            return key !== 'id' && originals[key] !== attributes[key]
        })
        .reduce((result: any = {}, key: string) => {

            result[key] = attributes[key]

            return result
        }, undefined)

        return changes
    }

    protected setTimestamps(timestamp: any): void {

        this.setAttributeRaw(this.updatedAtColumn, timestamp)

        if (!this.exists)
        {
            this.setAttributeRaw(this.createdAtColumn, timestamp)
        }
    }

    protected setPrimaryKeyForSave(query: QueryBuilder): QueryBuilder {

        return query.where(this.primaryKey, this.getPrimaryKeyId())
    }

    public getPrimaryKeyId(): any {

        const originalID = this.originals[this.primaryKey]

        if(originalID) {
            return originalID as any
        }

        return this.getAttribute(this.primaryKey)
    }

    public getPrimaryKey(): string {

        return this.primaryKey
    }

    protected async queryInsert(query: QueryBuilder): Promise<any> {  
        try {
            if (this.timestamp) {
                this.setTimestamps(query.now())
            }
            
            const [{id}] = await query.insert(this.getAttributes())

            this.setAttributeRaw(this.primaryKey, id);

            this.exists = true;

            return id;

        } catch(error) {

            throw new Error(error.message)
        } 
    }

    protected async queryUpdate(query: QueryBuilder): Promise<boolean | void> {   
        try {
            let updates = this.getAttributeChanges()

            if(!updates) 

                return false;

            if(this.timestamp) {

                this.setTimestamps(query.now())

                updates = this.getAttributeChanges()

            }

            await this.setPrimaryKeyForSave(query).update(updates);

            return true;

        } catch(error) {

            throw new Error(error.message)
        } 
    }

    protected async queryDelete(query: QueryBuilder): Promise<boolean> {
        try {

            await this.setPrimaryKeyForSave(query).delete()

        } catch(error) {

            throw new Error(error.message)
        } 

        return true;
    }

    public getConstructor(): any {
        return this.constructor as any
    }

    public getTable(): string {
        return this.getConstructor().table
    }

    public setRelative(key: string, value: any): void {

        this.relatives[key] = new Collection(value)
    }

    public getForeignKey(): string {
        return `${this.getTable()}_${this.primaryKey}`
    }

    public getAliasedKey(): string {
        return `${this.getTable()}.${this.primaryKey}`
    }

    protected belongsTo<D, R>(parent: ModelType<D, R>, foreignkey?: string, ownerkey?: string): BelongsToInstanceType<D, R> {

        const parentModel: any = new parent()

        const fk = foreignkey || parentModel.getForeignKey()

        const ok = ownerkey || parentModel.getAliasedKey()

        return new (BelongsTo as any)(parentModel.createBuilder(), this, fk, ok);
    }

    protected belongsToMany<D, R>(ToManyRelative: ModelType<D, R>, MediatorRelative: any, toManyForeignKey?: string, mediatorForeignKey?: string, localKey?: string): BelongsToManyInstanceType<D, R> {
        
        const toManyModel = new (ToManyRelative as any)()

        const mediatorModel = new MediatorRelative()
        
        const tmfk = toManyForeignKey || toManyModel.getForeignKey()
        
        const mfk = mediatorForeignKey || mediatorModel.getAliasedKey()

        const lk = localKey || this.getForeignKey()

        return new (BelongsToMany as any)(toManyModel.createBuilder(), mediatorModel.createBuilder(), this, tmfk, mfk, lk);
    }

    protected hasOneOrMany(relationType: any, relative: any, foreignkey?: string, localkey?: string) {

        const relativeModel = new relative()
        
        const fk = foreignkey || this.getForeignKey()

        const lk = localkey || this.getAliasedKey()

        return new relationType(relativeModel.createBuilder(), this, fk, lk);
    }

    protected hasOne<D, R>(relative: ModelType<D, R>, foreignkey?: string, localkey?: string): HasOneInstanceType<D, R> {

        return this.hasOneOrMany(HasOne, relative, foreignkey, localkey)
    }

    protected hasMany<D, R>(relative: ModelType<D, R>, foreignkey?: string, localkey?: string): HasManyInstanceType<D, R> {

        return this.hasOneOrMany(HasMany, relative, foreignkey, localkey)
    }

    public async save(pool?: ConnectionPool): Promise<string | boolean> {
        try {

            if(!this.creatable) {

                throw new Error(`${this.getConstructor().name} is not creatable`)
            }

            const builderWithQuery = this.createBuilder()

            if(pool) {

                builderWithQuery.pool(pool)
                
            }

            let saved

            if (this.exists) 
            {
                saved = await this.queryUpdate(builderWithQuery as any)
            }

            else
            {
                saved = await this.queryInsert(builderWithQuery as any)
            }

            this.syncAttrAndOrig()

            return saved

        } catch(error) {
            
            throw new Error(error.message)
        } 
    }

    public async delete(pool?: ConnectionPool): Promise<boolean> {
        try {

            if(!this.deletable) {

                throw new Error(`${this.getConstructor().name} is not deletable`)
            }

            const builderWithQuery = this.createBuilder()

            if (this.exists) {

                if(pool) {

                    builderWithQuery.pool(pool)

                }

                const deleted = await this.queryDelete(builderWithQuery as any)

                this.exists = false

                return deleted;
            }

            return false

        } catch(error) {

            throw new Error(error.message)
        } 
    }

    protected createBuilder() {

        return new Builder(this.createQuery(), this)
    }

    protected createQuery(): QueryBuilder {

        return new QueryBuilder(this.getConstructor(), this.use)
    }

    protected static createModel(): Model {

        return new this()
    }

    protected static createBuilderStatic() {

        return this.createModel().createBuilder()
    }

    public static async destroy(ids: string[], relatives?: string | string[] | {[name: string]: ($where: QueryInstanceType<any, any>) => void} | null, pool?: ConnectionPool): Promise<number> {
        try {
            // We'll initialize a destroyed here so we will return the total number of deletes
            const model = this.createModel()
            
            const builderWithQuery = model.createBuilder() as any

            // We will actually pull the models from the database table and call delete on
            // each of them individually so that their events get fired properly with a
            // correct set of attributes in case the developers wants to check these.
            const key = model.getPrimaryKey()

            if(relatives) {
                builderWithQuery.with(relatives)
            }
            
            if(pool) {
                builderWithQuery.pool(pool)
            }

            const collection = await builderWithQuery.whereIn(key, ids).delete()

            return collection

        } catch(error) {
            
            throw new Error(error.message)
        } 
    }

    private static findAbstraction(relatives?: RelativeArgAbstraction, pool?: ConnectionPool) {

        if(relatives instanceof ConnectionPool) {
            
            pool = relatives

            relatives = undefined

        }

        const builderWithQuery = this.createBuilderStatic()

        if(relatives) {

            builderWithQuery.with(relatives)
        }

        if(pool) {

            builderWithQuery.pool(pool)

        }

        return builderWithQuery
    }

    public static find(id: string | number, columns?: string[] | null, relatives?: RelativeArgAbstraction, pool?: ConnectionPool): Promise<ModelInstanceType<any, any> & CollectionInstanceType<any, any>> {   

        return this.findAbstraction(relatives, pool).find(id, columns || ["*"]);
    }

    public static findAndDelete(id: string | number, relatives?: RelativeArgAbstraction, pool?: ConnectionPool): Promise<{ itemsDeleted: number, relativeDeleted?: any}> {   

        return this.findAbstraction(relatives, pool).findAndDelete(id);
    }

    public static findAndSoftDelete(id: string | number, relatives?: RelativeArgAbstraction, pool?: ConnectionPool): Promise<{ itemsSoftDeleted: number, relativeSoftDeleted?: any}> {   

        return this.findAbstraction(relatives, pool).findAndSoftDelete(id);
    }
    
    public static findAndRestore(id: string | number, relatives?: RelativeArgAbstraction, pool?: ConnectionPool): Promise<{ itemsRestored: number, relativeRestored?: any}> {   

        return this.findAbstraction(relatives, pool).findAndRestore(id);
    }

    public static async findOrNew(id: string | number, columns?: string[] | null, pool?: ConnectionPool): Promise<ModelInstanceType<any, any> >{
        try {
            const model = await this.find(id, columns || ['*'], pool)

            if (model.hasItem) return model.getItems();

            return new this();
            
        } catch(error) {
        
            throw new Error(error.message)
        } 
    }
     
    public __iterator(): any {

        return this.toJSON()
    }

    public __get(key: string): string | object {

        if (typeof key === 'symbol')
            
            return this.toJSON()
        
        return this.getAttribute(key)
    }

    public __set(key: string, value: any): void {

        this.setAttribute(key, value)
    }

    public __isset(key: string): boolean {

        return key in this.attributes
    }

    public __unset(key: string): void {

        delete this.attributes[key]
    }

    public static __callStatic(method: string) {

        const instance = this.createBuilderStatic() as any;

        return (...parameters: any[]) => instance[method](...parameters)
    }

    public toString(): string {

        return JSON.stringify(this.toJSON(), null, 2);
    }

    public toJSON(): Record<string, any> {   
        const attributes = this.attributes
        let data = Object.keys(attributes).reduce<Record<string, any>>((result, key) => {

            if(this.isGuarded(key) === false) {
                result[key] = attributes[key]
            }

            return result
        }, {})

        const relatives = this.relatives

        for(let relate in relatives)
        {
            data[relate] = !!relatives[relate] ? relatives[relate].toJSON(): {}
        }

        return data
    }
}

export default Model